import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Text } from '@bonusly/design-system';

import { useBizyEvidenceAnalytics } from './bizy-evidence-analytics-context';

interface BizySourceDetailsProps {
	sourceDetails: string;
	caveats: string;
}

/**
 * Renders the Data Agent's SourceDetails node — the collapsible "How I got
 * this" provenance panel that trails a data answer: what was queried
 * (sourceDetails) and the limitations / assumptions (caveats). Mirrors the
 * native <details> disclosure the agent previously emitted as trailing
 * markdown, now driven by a typed node. Collapsed by default so it doesn't
 * compete with the answer.
 */
export function BizySourceDetails({ sourceDetails, caveats }: BizySourceDetailsProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const { conversationId, messageId } = useBizyEvidenceAnalytics();

	const handleToggle = (e: React.SyntheticEvent<HTMLElement>) => {
		const open = (e.currentTarget as HTMLDetailsElement).open;
		track(AnalyticsEvent.Bizy.SourceDetailsToggled, { open, conversationId, messageId, path: window.location.pathname });
	};

	return (
		<Box as="details" mb={4} fontSize="sm" color="text.muted" data-testid="bizy-source-details" onToggle={handleToggle}>
			<Box as="summary" cursor="pointer" fontSize="sm" fontWeight="semibold" color="text.default">
				{t('sourceDetails.heading')}
			</Box>
			<Box mt={2}>
				<Text fontSize="sm" fontWeight="semibold" color="text.default">
					{t('sourceDetails.sourceDetailsLabel')}
				</Text>
				<Text fontSize="sm" mb={2}>
					{sourceDetails || t('sourceDetails.noSourceDetails')}
				</Text>
				<Text fontSize="sm" fontWeight="semibold" color="text.default">
					{t('sourceDetails.caveatsLabel')}
				</Text>
				<Text fontSize="sm">{caveats || t('sourceDetails.noCaveats')}</Text>
			</Box>
		</Box>
	);
}
